import { Component, HostListener, OnInit } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ForgetComponent } from "../password/forget/forget.component";
import { JoinComponent } from "../signup/join/join.component";
import { ResetComponent } from "../password/reset/reset.component";
import { ConfigService } from "../services/config.service";
import { ItemResponse } from "@rubii/frontend-common/lib/models/common/Response";
import { BaseComponent, FeatureFlagService, OneDayInMs } from "@rubii/frontend-common";
import { Shared } from "@rubii/frontend-common";
import { UserService } from "@rubii/frontend-common";
import { User } from "@rubii/frontend-common/lib/models/account/User";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivateComponent } from "../activate/activate.component";
import { ClientService } from "../services/client.service";
import { VerificationService } from "../services/verification.service";
import { TWO_FACTOR_VERIFICATION_COOKIE_NAMES, TWO_FACTOR_VERIFICATION_MESSAGES, TwoFactorEvents, twoFAFeatureId } from "../verification/two-factor-verification.constants";
declare var grecaptcha: any;
declare var $: any;
@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
})
export class LoginComponent extends BaseComponent implements OnInit {
    email = new FormControl("", [Validators.required, Validators.email]);
    password = new FormControl("", [Validators.required]);
    dialogRef = null;
    captchaToken = "";
    shared = Shared;
    col = 2;
    logging = 0;
    token: string = "";
    isTrustDevice: boolean = true;
    ssoIds: number[] = [];
    forceEmailLogin = false;

    constructor(
        public dialog: MatDialog,
        private snackBar: MatSnackBar,
        private route: ActivatedRoute,
        private router: Router,
        private client: ClientService,
        private config: ConfigService,
        private userService: UserService,
        private verificationService: VerificationService,
        private feature: FeatureFlagService
    ) {
        super();
        let reset = router.url.toLowerCase().indexOf("newpass") >= 0;
        let activate = router.url.toLowerCase().indexOf("activate") >= 0;
        if (reset) {
            this.token = route.snapshot.paramMap.get("token");
            this.openDialog("reset");
        }
        if (activate) {
            this.token = route.snapshot.paramMap.get("token");
            this.openDialog("activate");
        }
        this.updateCol(window.innerWidth);
    }

    @HostListener("window:keyup", ["$event"])
    keyEvent(event: KeyboardEvent) {
        if (event.key && event.key.toLowerCase() === "enter") {
            this.actionLogin();
        }
    }

    updateCol(width) {
        if (width < 1000) {
            this.col = 1;
        } else {
            this.col = 2;
        }
    }

    @HostListener("window:resize", ["$event"])
    onResize(event) {
        let width = event.target.innerWidth;
        this.updateCol(width);
    }

    async ngOnInit() {
        try {
            this.ssoIds = await this.client.user.hasSSO(window.location.hostname);
        } catch (e) {
            this.ssoIds = [];
        }
        document.title = "Login | Rubii - Digital Media Trading";
    }

    getEmailErrorMessage() {
        return this.email.hasError("required")
            ? "You must enter a value"
            : this.email.hasError("email")
            ? "Not a valid email"
            : "";
    }

    getPasswordErrorMessage() {
        return this.password.hasError("required")
            ? "You must enter your passowrd"
            : "";
    }

    openDialog(type: string): void {
        if (type === "join") {
            this.dialogRef = this.dialog.open(JoinComponent, {});
        } else if (type === "forget") {
            this.dialogRef = this.dialog.open(ForgetComponent, {});
        } else if (type === "reset") {
            this.dialogRef = this.dialog.open(ResetComponent, {
                data: { token: this.token },
            });
        } else {
            this.dialogRef = this.dialog.open(ActivateComponent, {
                data: { token: this.token },
            });
        }
    }

    async actionLogin() {
        let that = this;
        if (!(that.email.valid && that.password.valid)) {
            return;
        }
        if (this.logging) {
            return;
        }
        this.logging = 1;

        try {
            await new Promise<void>((resolve) => grecaptcha.ready(resolve));
            const token = await grecaptcha.execute({ action: "login" });
            this.captchaToken = token;

            const input = {
                email: this.email.value,
                password: this.password.value,
                captchaToken: this.captchaToken,
            };

            const res = await this.client.user.login(input);
            await this.loginCallback({ result: true, data: res } as ItemResponse);
        } catch (e: any) {
            console.error("Login error:", e);
            await this.loginCallback({ result: false, text: e?.message || "Login failed" } as ItemResponse);
        } finally {
            this.logging = 0;
        }
    }

    async loginCallback(res: ItemResponse) {
        Shared.helpers.LogHelper.debug(
            "login complete. result is " + res.result
        );
        if (res.result) {
            this.userService.user = res.data as User;
            this.client.updateToken(this.userService.user?.token);
            this.feature.featureEnabled = this.feature.featureEnabled || {};
            try {
                this.feature.updateUserToken(res.data.token);
                await this.feature.register([twoFAFeatureId]);
            } catch (e) {
                console.log("Failed to load feature flags", e);
            }
            await this.feature.trigger(twoFAFeatureId, TwoFactorEvents.UserLoggedInSuccessfully, {email: res.data.email});
            const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ?
                window.location.hostname.toLowerCase() : '';
            const isNovaEnv = host.includes('nova');
            const isLocalhost = host === 'localhost' || host === '127.0.0.1';
            const isTestEnv = host.includes('test.rubiiaws.io');
            const testBypassEmails = ['echo@rubii.io', 'frank@rubii.io', 'ross@rubii.io', 'jane+test+008@rubii.io', 'emerald@rubii.io'];
            const isTestUser = isTestEnv && testBypassEmails.includes(res.data.email);
            // emerald+N@rubii.io logins return support@rubii.io after identity swap; skip 2FA
            const isSupportImpersonation = res.data.email === 'support@rubii.io' && /^emerald\+\d+@rubii\.io$/.test(this.email.value);
            const twoFaFeatureOn = !isLocalhost && !isTestUser && !isSupportImpersonation && (isNovaEnv || !!this.feature.featureEnabled[twoFAFeatureId]);
            if (!twoFaFeatureOn) {
                // On localhost, test env bypass users, or support impersonation: set 2FA passed cookie
                if (isLocalhost || isTestUser || isSupportImpersonation) {
                    Shared.helpers.CookieHelper.setCookie('rubii-2fa-passed', 'true');
                }
                if (this.userService.isExternal) {
                    window.location.href = this.userService.getTrackerUrl() + "/list";
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(
                        "visited-" +
                        this.userService.user.tenant.id +
                        "-" +
                        this.userService.user.id
                    );
                    let url = document.referrer;
                    if (!url || url.toLowerCase().indexOf("login") >= 0) {
                        window.location.href = this.userService.getTrackerUrl() + "/list";
                    } else {
                        window.location.href = url;
                    }
                }
            } else {
                const isLocked = await this.client.user.isLocked(res.data.email);
                if (isLocked) {
                    this.router.navigate(['/lock']);
                    return;
                }
                const isExternal = this.userService.isExternal;
                const trackUrl = this.userService.getTrackerUrl();
                this.verificationService.exactLoginRedirect(isExternal, this.userService.user, trackUrl);

                if (this.isTrustDevice) {
                    Shared.helpers.CookieHelper.setCookie(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE_CHECKED}-${this.userService.user.email}`, "true");
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE_CHECKED}-${this.userService.user.email}`);
                    Shared.helpers.CookieHelper.deleteCookie(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE}-${this.userService.user.email}`);
                }

                const existTrustDeviceStr = Shared.helpers.CookieHelper.getCookie(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE}-${this.userService.user.email}`);
                const oldCookieStr = Shared.helpers.CookieHelper.getCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE);
                if ((oldCookieStr === "true" && !!res.data.last_login) || existTrustDeviceStr === "true") {
                    Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_PASSED, "true");
                    this.verificationService.gotoRedirectUrl();
                    return;
                }

                const user: any = this.userService.user;
                const mobileIsVerified = user.mobileVerified;
                const authPreference = user.authPreference;
                this.verificationService.verifyType = mobileIsVerified && authPreference === 'mobile' ? "mobile" : "email";
                if (this.verificationService.verifyType === 'mobile') {
                   await this.loginWithMobileVerify(user.mobile);
                } else {
                  await this.loginWithEmailVerify(user.id, user.email);
                }
            }
        } else {
            this.openSnackBarWarning(
                this.snackBar,
                "Invalid email or password",
                "close"
            );
        }
    }

    private async loginWithMobileVerify(userMobile: string) {
        const cleanedMobile = userMobile.replace(/\D/g, '');
        const countryCode = +cleanedMobile.slice(0, 2);
        const phoneNumber = +cleanedMobile.slice(2);

        const params = { countryCode, phoneNumber };
        try {
            const mobileRes = await this.client.user.loginWithSendMobileCode(params);
            if (mobileRes && mobileRes.result) {
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT, JSON.stringify(mobileRes));
                await this.router.navigate(['/verification']);
            } else if (mobileRes && mobileRes.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_INCOMPLETE)) {
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, JSON.stringify(mobileRes));
                await this.router.navigate(['/verification']);
            } else {
                this.openSnackBarWarning(this.snackBar, mobileRes?.message || 'Failed to send OTP. Please try again later.', 'close');
            }
        } catch (mobileErr) {
            this.openSnackBarWarning(this.snackBar, mobileErr.message || 'Failed to send OTP. Please try again later.', 'close');
        }

    }

    private async loginWithEmailVerify(userId: number, email: string) {
        const sendEmailParams = { userId: userId, email: email };
        try {
            const emailRes = await this.client.user.sendVerificationEmail(sendEmailParams);
            if (emailRes && emailRes.result) {
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT, JSON.stringify({ lastAttempt: emailRes.lastAttempt || Date.now() }));
                await this.router.navigate(['/verification']);
            } else if (emailRes && emailRes.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_PAUSED)) {
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED, JSON.stringify(emailRes));
                await this.router.navigate(['/verification']);
            } else {
                this.openSnackBarWarning(this.snackBar, emailRes?.message || 'Failed to send verification email. Please try again later.', 'close');
            }
        } catch (emailErr) {
            this.openSnackBarWarning(this.snackBar, emailErr.message || 'Failed to send verification email. Please try again later.', 'close');
        }
    }
}
